import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { createStore } from "../../../slices/storeSlice";
import { useCompleteStep } from "../hooks/useCompleteStep";
import { toast } from "react-toastify";
import { CheckCircle } from "lucide-react";

const validationSchema = Yup.object({
  name: Yup.string().required("Store name is required"),
  location: Yup.string(),
  contactInfo: Yup.string(),
});

const StoreSetupStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const stores = useAppSelector((s) => s.stores.stores);
  const adding = useAppSelector((s) => s.stores.adding);
  const { complete, onboarding, stepIndex } = useCompleteStep("store_setup");

  useEffect(() => {
    if (stores.length > 0 && !onboarding.completedSteps[stepIndex]) {
      complete();
    }
  }, [stores, onboarding.completedSteps, stepIndex, complete]);

  const formik = useFormik({
    initialValues: { name: "", location: "", contactInfo: "" },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(createStore(values)).unwrap();
        toast.success("Store created successfully!");
      } catch (err: any) {
        toast.error(err || "Failed to create store");
      }
    },
  });

  if (onboarding.completedSteps[stepIndex]) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-800">Store Created!</h3>
        <p className="text-gray-500 mt-2">Your store "{stores[0]?.name}" is ready.</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <h3 className="text-lg font-semibold mb-2">Create Your First Store</h3>
      <p className="text-gray-500 text-sm mb-6">
        Set up your store so you can start managing inventory and making sales.
      </p>

      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <div>
          <label className="form-label">Store Name *</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g. Main Branch"
            {...formik.getFieldProps("name")}
          />
          {formik.touched.name && formik.errors.name && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
          )}
        </div>

        <div>
          <label className="form-label">Location</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g. Accra, Ghana"
            {...formik.getFieldProps("location")}
          />
        </div>

        <div>
          <label className="form-label">Contact Info</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g. +233 XX XXX XXXX"
            {...formik.getFieldProps("contactInfo")}
          />
        </div>

        <button type="submit" className="form-btn w-full" disabled={adding}>
          {adding ? "Creating..." : "Create Store"}
        </button>
      </form>
    </div>
  );
};

export default StoreSetupStep;
